import { restClient } from '#app/shared/apis/restClient'
import { AuthUserRelationship } from '#app/shared/contexts/AuthContext'
import { PagePaths } from '#app/shared/navigation/pagePaths'
import type { AuthenticatedUser } from '#app/shared/types/UserTypes'
import type { History } from 'history'
import Cookies from 'js-cookie'
import type JwtPayload from 'jwt-decode'
import type { AuthStateViewType, IdTokenWithTrueNorthExtensions } from '../types/AuthTypes'

export const KeyCypressSignInTest = 'cypressSignInTest'
export const KeyCypressIdToken = 'cypressIdToken'

const KeyCookieAccessToken = 'access_token'
const KeyCookieAccessTokenExpiry = 'access_token_expiry'

/**
 * JWT payload with permissions. TrueNorth extension in Auth0.
 */
export type JwtPayloadWithPermissions = {
  user_id: string
  carrier_id?: string
  broker_id?: string
  app_roles: string[]
  permissions: string[]
  mc_number?: string
} & typeof JwtPayload

/**
 * Check if the app is running in Cypress.
 */
export function isCypress() {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  return !!(window as any).Cypress
}

/**
 * Indicates that Cypress is running a sign-in test.
 * @returns
 */
export function isCypressSignInTest() {
  return window.localStorage.getItem(KeyCypressSignInTest) === 'true'
}

/**
 * Redirect to the sign-in page.
 * @param history
 */
export function redirectToSignin(history: History) {
  resetAuthToken()
  const currentPath = history.location.pathname

  // Prevent redirect loops by not setting returnTo if already on sign-in page
  const search = currentPath === PagePaths.SignIn ? '' : `?returnTo=${currentPath}`

  // replace history so that the user can't navigate back to the page they were on
  history.replace({
    pathname: PagePaths.SignIn,
    search
  })
}

export function navigateToHomePage(force = false) {
  if (!force && window.location.pathname === PagePaths.Home) {
    return
  }
  window.location.assign(PagePaths.Home)
}

/**
 * Navigate the application window to the sign-in page.
 * @param force Force the navigation to the sign-in page.
 * @returns
 */
export function navigateToSignIn(force = false) {
  if (!force && window.location.pathname === PagePaths.SignIn) {
    return
  }
  window.location.assign(PagePaths.SignIn)
}

export function navigateToServerDown(history: History) {
  history.push(PagePaths.ServerDown)
}

export function navigateToNoAccessPage(history: History) {
  history.push(PagePaths.NoAccess)
}

/**
 * Get the authenticated user's information.
 */
export async function getApiAuthenticatedUser() {
  const response = await restClient.get<AuthenticatedUser>(
    '/api/authenticated-user'
  )
  return response
}

/**
 * Returns the Authorization Bearer header value.
 * @returns Header value or undefined if no token present.
 */
export const getBearerHeader = () => {
  const token = Cookies.get(KeyCookieAccessToken)

  if (!token) {
    return undefined
  }
  return `Bearer ${token}`
}

/**
 * Resets the user token.
 */
export const resetAuthToken = () => {
  Cookies.remove(KeyCookieAccessToken)
  Cookies.remove(KeyCookieAccessTokenExpiry)
}

/**
 * Set's the user token.
 * @param token Token from Auth0.
 */
export const setAuthToken = (token: string) => {
  Cookies.set(KeyCookieAccessToken, token)
}

/**
 * Retreives the user's access token.
 * @returns
 */
export const getAuthToken = () => {
  return Cookies.get(KeyCookieAccessToken)
}

/** Returns the id_token authenticated under Cypress. */
export const getCypressIdToken = () => {
  return window.localStorage.getItem(KeyCypressIdToken)
}

/** Returns whether cypress has authenticated user. */
export const isCypressAuthenticated = () => {
  // When isCypressSignInTest is true, Cypress has not pre-authenticated user.
  if (isCypressSignInTest()) {
    return false
  }
  const idToken = getCypressIdToken()
  return isCypress() && idToken !== null && idToken.trim() !== ''
}

/**
 * Auth0 claim roles.
 */
export enum ClaimRoles {
  Broker = 'Broker',
  BrokerAdmin = 'Broker Admin',
  Staff = 'Staff',
  Carrier = 'Carrier'
}

/**
 * Auth0 claim permissions.
 */
export enum ClaimPermissions {
  UseCarrierApp = 'use:carrier_app',
  UseBrokerApp = 'use:broker_app'
}

/**
 * Returns whether the user's Auth0 claim roles include any from a set of roles.
 * @param roles
 * @param claims Claims from Auth0, including roles.
 * @returns
 */
export const authClaimsIncludesAnyRole = (
  claims: IdTokenWithTrueNorthExtensions | null,
  roles: ClaimRoles[]
) => {
  if (!claims || claims?.app_roles?.length === 0) {
    return false
  }

  return roles.some((role) => claims.app_roles?.includes(role))
}

/**
 * Returns whether the user's Auth0 claim roles include the specified role.
 * @param role
 * @param claims Claims from Auth0, including roles.
 * @returns
 */
export const authClaimsHasRole = (
  claims: IdTokenWithTrueNorthExtensions | null,
  role: ClaimRoles
) => {
  if (!claims || claims?.app_roles?.length === 0) {
    return false
  }

  return claims.app_roles?.includes(role)
}

/** Returns whether the user has the specified permission. */
export const authPermissionsHavePermission = (
  permissions: string[] | null,
  permission: string
) => {
  return permissions?.includes(permission)
}

/**
 * Checks whether the user's account type is supported.
 * @param authState
 * @returns
 */
export const checkSupportedAccountType = (authState: AuthStateViewType) => {
  if (!authState.authIsAuthenticated) {
    throw new Error(
      'checkSupportedAccountType: authState.authIsAuthenticated is required'
    )
  }

  const { authUserClaims } = authState

  // Staff users always have access
  if (authClaimsHasRole(authUserClaims, ClaimRoles.Staff)) {
    return true
  }

  // Broker
  if (
    authClaimsHasRole(authUserClaims, ClaimRoles.BrokerAdmin)
    || authClaimsHasRole(authUserClaims, ClaimRoles.Broker)
  ) {
    return true
  }

  // Carrier
  if (authClaimsHasRole(authUserClaims, ClaimRoles.Carrier)) {
    return true
  }

  return false
}

/**
 * Checks whether the user's account type is supported.
 * @param authState
 * @returns
 */
export const checkSupportedBrokerAccountType = (
  authState: AuthStateViewType
) => {
  if (!authState.authIsAuthenticated) {
    throw new Error(
      'checkSupportedBrokerAccountType: authState.authIsAuthenticated is required'
    )
  }

  const { authUserClaims, authContext, authPermissions } = authState
  const { userRelationship } = authContext || {
    userRelationship: AuthUserRelationship.None
  }

  /*
  if (!authUserClaims?.user_id) {
    console.error(
      `User's token missing required user_id value (${authContext?.userEmail ?? 'unknown'})`,
    );
    return false;
  }
  */

  const hasBrokerAppPermission = authPermissions
    && authPermissionsHavePermission(
      authPermissions,
      ClaimPermissions.UseBrokerApp
    )

  // must have the correct Roles and a relationship to a broker.
  return (
    (hasBrokerAppPermission
      || authClaimsHasRole(authUserClaims, ClaimRoles.BrokerAdmin)
      || authClaimsHasRole(authUserClaims, ClaimRoles.Broker))
    && userRelationship === AuthUserRelationship.Broker
  )
}

/**
 * Checks whether the user's account type is a carrier.
 * @param authState
 * @returns
 */
export const checkSupportedCarrierAccountType = (
  authState: AuthStateViewType
) => {
  if (!authState.authIsAuthenticated) {
    throw new Error(
      'checkSupportedCarrierAccountType: authState.authIsAuthenticated is required'
    )
  }

  const { authUserClaims, authPermissions } = authState

  // First check if user has Carrier role
  const hasCarrierRole = authClaimsHasRole(authUserClaims, ClaimRoles.Carrier)

  // Then check if user has carrier app permission
  const hasCarrierAppPermission = authPermissions
    && authPermissionsHavePermission(
      authPermissions,
      ClaimPermissions.UseCarrierApp
    )

  // Return true if either condition is met
  return hasCarrierRole || hasCarrierAppPermission
}

/**
 * Checks whether the user's account type is a staff.
 * @param authState
 * @returns
 */
export const checkSupportedStaffAccountType = (
  authState: AuthStateViewType
) => {
  if (!authState.authIsAuthenticated) {
    throw new Error(
      'checkSupportedStaffAccountType: authState.authIsAuthenticated is required'
    )
  }
  return authClaimsHasRole(authState.authUserClaims, ClaimRoles.Staff)
}

/**
 * Checks whether the user has the trucklist permission.
 * @param context
 * @returns
 */
export const checkHasTrucklistPerm = (context: AuthStateViewType): boolean => {
  if (context.authPermissions?.includes('read:trucklist')) {
    return true
  }

  return false
}
